import AggregateError from 'es-aggregate-error';
import type { TFunction } from 'i18next';

import type {
  CustomField,
  FieldDataType,
  TEmailInteractionType,
  TOtherActivityScope,
  TSegmentExportMode,
} from '@feathr/blackbox';
import type { IToastAction } from '@feathr/components';
import { showError, toast } from '@feathr/components';
import { RachisError } from '@feathr/rachis';

import defaultBreadcrumbFields from '@feathr/blackbox/fixtures/defaultBreadcrumbFields.json';
import defaultPersonFields from '@feathr/blackbox/fixtures/defaultPersonFields.json';

export type {
  IEmailActivityExportPayload,
  IOtherActivitiesExportPayload,
  TEmailInteractionType,
  TOtherActivityScope,
} from '@feathr/blackbox';

export const DEFAULT_PERSON_FIELD_IDS: readonly string[] = ['name', 'email'];

export const DEFAULT_BREADCRUMB_FIELD_IDS: readonly string[] = ['d_c', 'loc_url'];

/**
 * Person fields that must not be exportable from the Person tab once
 * `showEmailActivityExport` is on for an account (Feathr/shrike#9188), per the PII
 * decisions on Feathr/shrike#9154: `geoip.lat`/`geoip.lng` read as precise location
 * tracking once they leave the platform in a file, and `date_of_birth` can't have its
 * year stripped in a way that survives an Excel round-trip. This is a UI-only gate —
 * the backend (Feathr/anhinga#8518) is the actual enforcement point.
 *
 * Not a copy of the backend list. anhinga's `NEVER_EXPORTABLE_PERSON_FIELDS` is the
 * source of truth and is longer; these are only the entries `defaultPersonFields.json`
 * actually offers, so a field belongs here only once the picker offers it. The backend
 * keeps a separate `NEVER_EXPORTABLE_BREADCRUMB_FIELDS`, because a breadcrumb records
 * the same data under its own names.
 */
export const NON_EXPORTABLE_PERSON_FIELD_IDS: readonly string[] = [
  'date_of_birth',
  'geoip.lat',
  'geoip.lng',
];

export interface IPersonFieldOption {
  id: string;
  name: string;
  type: FieldDataType;
}

/** Backend `export_mode` discriminator: one row per person vs. one row per activity. */
export type TExportMode = Extract<TSegmentExportMode, 'persons' | 'breadcrumbs'>;

export interface IExportTypeOption {
  id: TExportMode;
  name: string;
}

export const DEFAULT_EMAIL_INTERACTION_TYPES: readonly TEmailInteractionType[] = [
  'delivery',
  'open',
];

export interface IInteractionTypeOption {
  id: TEmailInteractionType;
  label: string;
}

const HTTP_STATUS_CONFLICT = 409;

/**
 * True when the export was rejected because an identical one is already pending
 * or running. The backend keys duplicate detection on the account, export type,
 * and a hash of the frozen query, so changing the interaction types or the date
 * window yields a distinct export instead of a conflict.
 *
 * The status is coerced because the two sides of the wire disagree about its
 * type: `RachisError.status` is typed `number` but `JsonApiError.status` is
 * typed `string`, and `wretch` passes the value straight through without
 * converting it. A strict comparison would silently miss `'409'`.
 */
export function isDuplicateExportError(error: unknown): boolean {
  if (!(error instanceof AggregateError)) {
    return false;
  }
  return error.errors.some(
    (entry) => entry instanceof RachisError && Number(entry.status) === HTTP_STATUS_CONFLICT,
  );
}

/**
 * Flattens each tab's error slices into the single list the modal renders, dropping
 * the ones that are `undefined`. Every tab validates a different set of controls, so
 * the caller decides which slices to pass and in what order.
 *
 * @param groups - Error slices, in the order they should appear.
 * @returns Every error from the defined slices, concatenated.
 *
 * @example
 * collectValidationErrors(form.emailError, form.exportNameError, fieldsError);
 */
export function collectValidationErrors(...groups: Array<string[] | undefined>): string[] {
  return groups.flatMap((group) => group ?? []);
}

export interface IDuplicateExportErrorHandlerOptions {
  /** Translation function from `useTranslation`. */
  t: TFunction;
  /**
   * What this surface can change to produce a different export. Each submit path
   * offers different controls, so none of them can share one sentence.
   */
  description: string;
  /**
   * Links the toast at the export that is blocking this one. Omit it on surfaces
   * where the exports dashboard is unreachable, or the link resolves to nothing.
   */
  action?: IToastAction;
}

/**
 * Builds the `onError` handler an export submit path hands to
 * `useExportModal.handleExport`.
 *
 * A 409 is the one export failure the generic "Couldn't export data. Try again."
 * copy actively misleads on: the backend already has an identical export running
 * for this requester, so retrying cannot clear it. Every other failure keeps the
 * API's own message through `showError`.
 *
 * @param options - Translation function, per-surface advice, and optional toast action.
 * @returns The `onError` callback for `IHandleExportOptions`.
 *
 * @example
 * onError: createDuplicateExportErrorHandler({
 *   t,
 *   description: t('Wait for it to finish before exporting this group again.'),
 *   action: successToastAction,
 * });
 */
export function createDuplicateExportErrorHandler({
  t,
  description,
  action,
}: IDuplicateExportErrorHandlerOptions): (error: unknown) => void {
  return (error: unknown): void => {
    if (isDuplicateExportError(error)) {
      toast(
        {
          title: t('An identical export is already in progress.'),
          description,
          ...(action ? { action } : {}),
        },
        { type: 'error' },
      );
      return;
    }
    showError(error, t);
  };
}

export function getInteractionTypeOptions(t: (key: string) => string): IInteractionTypeOption[] {
  return [
    { id: 'delivery', label: t('Delivery') },
    { id: 'open', label: t('Open') },
    { id: 'click', label: t('Click') },
  ];
}

/**
 * Returns `<groupName>_<YYYY-MM-DD>` using today's UTC date.
 * Falls back to the literal `group` when the trimmed name is empty.
 */
export function resolveExportNameTemplate(groupName: string, today: Date = new Date()): string {
  const safeName = groupName.trim().length > 0 ? groupName : 'group';
  const datePart = today.toISOString().split('T')[0];
  return `${safeName}_${datePart}`;
}

/** Custom fields get the `custom.<f_key>` id prefix per the export contract. */
export function getPersonFieldOptions(customFields: CustomField[]): IPersonFieldOption[] {
  const defaults: IPersonFieldOption[] = defaultPersonFields.map(({ id, name, type }) => ({
    id,
    name,
    type: type as FieldDataType,
  }));

  const customOptions: IPersonFieldOption[] = customFields.map((field) => ({
    id: `custom.${field.get('f_key')}`,
    name: field.get('u_key'),
    type: field.get('data_type'),
  }));

  return [...defaults, ...customOptions];
}

/** Filters out `NON_EXPORTABLE_PERSON_FIELD_IDS` — a UI-only gate, see its doc comment. */
export function excludeNonExportablePersonFields(
  options: IPersonFieldOption[],
): IPersonFieldOption[] {
  return options.filter((option) => !NON_EXPORTABLE_PERSON_FIELD_IDS.includes(option.id));
}

export interface IOtherActivityFieldOption {
  id: string;
  name: string;
  type: FieldDataType;
}

export const DEFAULT_OTHER_ACTIVITY_FIELD_IDS: readonly string[] = ['loc_url', 'd_c'];

export interface IOtherActivityScopeOption {
  id: TOtherActivityScope;
  label: string;
  tooltip: string;
}

export interface IOtherActivityExportColumns {
  export_fields: string[];
  export_field_headers: string[];
}

/**
 * Zips the selected field ids into the positionally-aligned `export_fields` /
 * `export_field_headers` arrays the backend expects.
 *
 * Order follows `selectedFieldIds`, not the option list, so the CSV columns come
 * out in the order the user picked them. An id with no matching option — a
 * custom field selected before its fetch resolved, say — keeps its place and
 * falls back to the raw id as its header, because dropping it would silently
 * export fewer columns than the user asked for.
 */
export function getOtherActivityExportColumns(
  selectedFieldIds: readonly string[],
  fieldOptions: readonly IOtherActivityFieldOption[],
): IOtherActivityExportColumns {
  const nameById = new Map(fieldOptions.map((option) => [option.id, option.name]));
  return {
    export_fields: [...selectedFieldIds],
    export_field_headers: selectedFieldIds.map((id) => nameById.get(id) ?? id),
  };
}

export function getOtherActivityScopeOptions(
  t: (key: string) => string,
): IOtherActivityScopeOption[] {
  return [
    { id: 'group', label: t('Group'), tooltip: t('Shows only activity related to this group') },
    {
      id: 'all',
      label: t('All'),
      tooltip: t("Shows activity across this person's entire history"),
    },
  ];
}

/*
 * Custom field ids are prefixed by the collection they belong to. Breadcrumbs use
 * `cust_params.`; only person fields use `custom.`. Both the Other Activities tab
 * and the legacy Activity export post `export_mode: 'breadcrumbs'`, so both must
 * use the breadcrumb prefix — the backend resolves each `export_fields` entry as
 * a dotted path against the serialized breadcrumb, which has no `custom` key, so
 * a mis-prefixed id yields a column that is empty on every row rather than an error.
 */
const BREADCRUMB_CUSTOM_FIELD_PREFIX = 'cust_params';

export function getOtherActivityFieldOptions(
  customFields: CustomField[],
): IOtherActivityFieldOption[] {
  const defaults: IOtherActivityFieldOption[] = defaultBreadcrumbFields.map((f) => ({
    id: f.id,
    name: f.name,
    type: f.type as FieldDataType,
  }));

  const customOptions: IOtherActivityFieldOption[] = customFields.map((field) => ({
    id: `${BREADCRUMB_CUSTOM_FIELD_PREFIX}.${field.get('f_key')}`,
    name: field.get('u_key'),
    type: field.get('data_type'),
  }));

  return [...defaults, ...customOptions];
}

export function getBreadcrumbFieldOptions(customFields: CustomField[]): IPersonFieldOption[] {
  const defaults: IPersonFieldOption[] = defaultBreadcrumbFields.map(({ id, name, type }) => ({
    id,
    name,
    type: type as FieldDataType,
  }));

  const customOptions: IPersonFieldOption[] = customFields.map((field) => ({
    id: `${BREADCRUMB_CUSTOM_FIELD_PREFIX}.${field.get('f_key')}`,
    name: field.get('u_key'),
    type: field.get('data_type'),
  }));

  return [...defaults, ...customOptions];
}

export function getExportTypeOptions(t: (key: string) => string): IExportTypeOption[] {
  return [
    { id: 'persons', name: t('People') },
    { id: 'breadcrumbs', name: t('Activity') },
  ];
}
